import { alpha, Box, useTheme } from '@mui/material';
import { type ReactNode, useCallback, useMemo } from 'react';
import { type Layout, Responsive, WidthProvider } from 'react-grid-layout';

import { TEMPLATES } from './templates.js';
import { useUserPreference } from '../../hooks/use-user-preference.js';

const ResponsiveGridLayout = WidthProvider(Responsive);

export const LAYOUT_BREAKPOINTS = { lg: 1200, md: 900, sm: 600, xs: 0 } as const;
export const LAYOUT_COLS = { lg: 12, md: 8, sm: 4, xs: 2 } as const;
const ROW_HEIGHT = 80;

export interface LayoutGridItem {
  id: string;
  /** Override the auto-pack default for this tile. RGL applies this only when
   * the persisted layout has no entry for `id` (e.g. a newly added camera). */
  defaultLayout?: Partial<Pick<Layout, 'x' | 'y' | 'w' | 'h' | 'minW' | 'minH'>>;
}

interface Props {
  /** Storage scope — must round-trip through `useUserPreference`. */
  scope: string;
  items: LayoutGridItem[];
  editMode: boolean;
  children: ReactNode;
  /** Optional callback after each persisted layout write. */
  onLayoutChange?: (layout: Layout[]) => void;
}

const DRAG_HANDLE_SELECTOR = '.layout-drag-handle';

const LayoutGrid = ({ scope, items, editMode, children, onLayoutChange }: Props) => {
  const theme = useTheme();
  const tileIds = useMemo(() => items.map((i) => i.id), [items]);
  const defaultLayout = useMemo(
    () => buildDefaultLayout(items),
    [items],
  );

  const [savedLayout, setSavedLayout] = useUserPreference<Layout[]>(`layout:${scope}`, defaultLayout);

  // Reconcile persisted layout against current tileIds:
  //   - drop entries for tiles that no longer exist
  //   - auto-place tiles added since the layout was saved
  const resolved = useMemo<Layout[]>(() => {
    const persistedById = new Map(savedLayout.map((l) => [l.i, l]));
    const present = tileIds.map((id) => persistedById.get(id)).filter((l): l is Layout => !!l);
    const occupiedYMax = present.reduce((max, l) => Math.max(max, l.y + l.h), 0);
    const missing = items
      .filter((it) => !persistedById.has(it.id))
      .map((it, idx) => ({
        i: it.id,
        x: (idx % 3) * 4,
        y: occupiedYMax + Math.floor(idx / 3) * 3,
        w: it.defaultLayout?.w ?? 4,
        h: it.defaultLayout?.h ?? 3,
        minW: it.defaultLayout?.minW ?? 2,
        minH: it.defaultLayout?.minH ?? 2,
      }));
    return [...present, ...missing];
  }, [savedLayout, tileIds, items]);

  const handleChange = useCallback(
    (next: Layout[]) => {
      // RGL fires `onLayoutChange` on the initial mount with the *initial*
      // layout we passed in. Suppress writes if nothing actually changed.
      const same =
        next.length === resolved.length &&
        next.every((n) => {
          const r = resolved.find((x) => x.i === n.i);
          return r && r.x === n.x && r.y === n.y && r.w === n.w && r.h === n.h;
        });
      if (same) return;
      setSavedLayout(next.map((l) => ({ i: l.i, x: l.x, y: l.y, w: l.w, h: l.h, minW: l.minW, minH: l.minH })));
      onLayoutChange?.(next);
    },
    [resolved, setSavedLayout, onLayoutChange],
  );

  return (
    <Box
      sx={{
        ...(editMode && {
          outline: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
          outlineOffset: 4,
          borderRadius: 1,
        }),
        '& .react-grid-item.react-grid-placeholder': {
          bgcolor: alpha(theme.palette.primary.main, 0.25),
          borderRadius: 1,
        },
        // RGL's default handle is a tiny background-image triangle that gets
        // visually buried under the existing bottom controls strip (zIndex 7).
        // Override only when editing — give it a visible primary-tinted square
        // with a diagonal arrow, and lift it above every overlay.
        '& .react-resizable-handle': editMode
          ? {
              zIndex: 12,
              width: 22,
              height: 22,
              right: 4,
              bottom: 4,
              backgroundImage: 'none',
              backgroundColor: alpha(theme.palette.primary.main, 0.85),
              borderRadius: 1,
              cursor: 'se-resize',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 4,
                borderRight: `2px solid ${theme.palette.primary.contrastText}`,
                borderBottom: `2px solid ${theme.palette.primary.contrastText}`,
              },
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            }
          : { display: 'none' },
      }}
    >
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: resolved }}
        breakpoints={LAYOUT_BREAKPOINTS}
        cols={LAYOUT_COLS}
        rowHeight={ROW_HEIGHT}
        margin={[8, 8]}
        containerPadding={[0, 0]}
        isDraggable={editMode}
        isResizable={editMode}
        draggableHandle={DRAG_HANDLE_SELECTOR}
        resizeHandles={['se']}
        compactType="vertical"
        onLayoutChange={handleChange}
      >
        {children}
      </ResponsiveGridLayout>
    </Box>
  );
};

function buildDefaultLayout(items: LayoutGridItem[]): Layout[] {
  return TEMPLATES.auto.build(items.map((i) => i.id));
}

export default LayoutGrid;
